import React, { useEffect, useMemo, useCallback, useState } from 'react';
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  Tooltip,
  InputBase,
  Select,
  MenuItem,
  useTheme,
} from '@mui/material';
import PublicAccessPolicyEditor from '../access/PublicAccessPolicyEditor';
import GroupIcon from '@mui/icons-material/Group';
import PublicIcon from '@mui/icons-material/Public';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  ISSUE_LEVEL,
  validateReviewTaskAccess,
} from '../../utils/reviewTaskAccessValidation';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { getDynamicReviewTaskStore } from './useDynamicReviewTaskStore';
import DynamicFormFieldDesigner from '../pages/forms/DynamicFormFieldDesigner';
import CoverImageSetting from '../pages/forms/CoverImageSetting';
import W86SegmentedTabs from '../common/W86SegmentedTabs';
import { useFormPalette } from '../pages/forms/DynamicFormPrimitives';
import { NODE_UI } from '../NodeUI/nodeUiStyles';
import NodePlaceholderAutocomplete from '../NodeUI/NodePlaceholderAutocomplete';
import NodeEmailRecipientPicker from '../NodeUI/NodeEmailRecipientPicker';
import NodeRichTextEditor from '../NodeUI/NodeRichTextEditor';
import useTaskInboxStore from '../pages/useTaskInboxStore';
import FlowSettingsBlock from '../FormFlow/FlowSettingsBlock';
import StepperTitleInput from '../FormFlow/StepperTitleInput';
import useUpstreamFlowEntries from '../FormFlow/useUpstreamFlowEntries';
import CreationOutputPlaceholderField from '../CreationOutputPlaceholder/CreationOutputPlaceholderField';

/* ── Assignment mode constants───────────────────────────────────────── */

// Backend persists three modes (ONE_PERSON / MANY_PEOPLE / ANYONE_ELIGIBLE),
// but ONE_PERSON and MANY_PEOPLE are functionally identical at runtime — both
// just become an assignee list. We expose a single "Specific users" UI mode
// and derive the backend activeMode from the assignee count on save.
const BACKEND_MODES = {
  ONE_PERSON: 'ONE_PERSON',
  MANY_PEOPLE: 'MANY_PEOPLE',
  ANYONE_ELIGIBLE: 'ANYONE_ELIGIBLE',
};

const UI_MODES = {
  SPECIFIC_USERS: 'SPECIFIC_USERS',
  ANYONE_ELIGIBLE: 'ANYONE_ELIGIBLE',
};

const UI_MODE_CONFIG = [
  {
    value: UI_MODES.SPECIFIC_USERS,
    label: 'Specific users',
    icon: GroupIcon,
    hint: 'Pick one or more people to review this task',
  },
  {
    value: UI_MODES.ANYONE_ELIGIBLE,
    label: 'Anyone eligible',
    icon: PublicIcon,
    hint: 'Any eligible user can claim the task, or allow anonymous public-link submissions',
  },
];

function toUiMode(backendMode) {
  if (backendMode === BACKEND_MODES.ANYONE_ELIGIBLE) return UI_MODES.ANYONE_ELIGIBLE;
  return UI_MODES.SPECIFIC_USERS;
}

function backendModeForSpecificUsers(count) {
  return count > 1 ? BACKEND_MODES.MANY_PEOPLE : BACKEND_MODES.ONE_PERSON;
}

// Legacy access mode descriptions kept for reference; UI now uses PublicAccessPolicyEditor

const RESPONSE_MODE_DESCRIPTIONS = {
  FILL_SUBMIT: 'Assignee fills out the form and submits. No drafts are saved on the server.',
  FILL_REEDIT: 'Assignee can save drafts and return via a unique URL to continue editing. Drafts are stored on the server and automatically expire after 30 days.',
  VIEW_ONLY: 'Assignee can view the form but cannot edit or submit responses.',
};

/* ── Helpers ─────────────────────────────────────────────────────────── */

function getResponsibility(taskConfig) {
  return taskConfig?.responsibility || {};
}

function escapeHtml(value) {
  if (value == null) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Build the default email subject template shown to the user when they first
 * enable notifications. Mirrors the server-side fallback
 * ({@code DynamicReviewTaskService.sendReviewTaskEmail}) so what the user sees
 * in the composer matches what would be sent.
 */
function buildDefaultEmailSubject(title) {
  const trimmed = typeof title === 'string' ? title.trim() : '';
  return trimmed || 'Create Task';
}

/**
 * Build the default email body template. The `!{taskURL}` token is recognized
 * by {@code TaskLib.processEmailBody} on the backend and replaced with an
 * anchor to the task form at send time.
 */
function buildDefaultEmailBody(objective) {
  const objHtml = escapeHtml(typeof objective === 'string' ? objective : '').trim();
  const objectiveBlock = objHtml ? `<p>${objHtml}</p>` : '';
  return `${objectiveBlock}<p>Please complete the Create Task here: !{taskURL}</p>`;
}

/* ── Section wrapper ─────────────────────────────────────────────────── */

function SettingsSection({ title, children, palette }) {
  return (
    <Box
      sx={{
        border: `1px solid ${palette.border}`,
        borderRadius: NODE_UI.borderRadius,
        backgroundColor: palette.surface,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          px: '12px',
          py: '7px',
          backgroundColor: palette.surfaceSubtle,
          borderBottom: `1px solid ${palette.border}`,
        }}
      >
        <Typography
          sx={{
            fontSize: NODE_UI.fontSize.value,
            fontWeight: 600,
            color: palette.textPrimary,
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: '12px', py: '10px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {children}
      </Box>
    </Box>
  );
}

/* ── Guardrail banners ────────────────────────────────────────────────
 *
 * Renders the output of validateReviewTaskAccess() as a stack of compact
 * banners above the Assignment section. Errors are red, warnings amber,
 * info is a subtle neutral note. Rendering every issue (rather than
 * collapsing into one) lets the builder fix each problem in place.
 */

const BANNER_COLORS = {
  [ISSUE_LEVEL.ERROR]: {
    bg: 'rgba(239, 68, 68, 0.08)',
    border: 'rgba(239, 68, 68, 0.45)',
    fg: '#b91c1c',
    darkFg: '#fca5a5',
    Icon: ErrorOutlineIcon,
  },
  [ISSUE_LEVEL.WARNING]: {
    bg: 'rgba(234, 179, 8, 0.08)',
    border: 'rgba(234, 179, 8, 0.45)',
    fg: '#854d0e',
    darkFg: '#fde68a',
    Icon: WarningAmberIcon,
  },
  [ISSUE_LEVEL.INFO]: {
    bg: 'rgba(59, 130, 246, 0.06)',
    border: 'rgba(59, 130, 246, 0.35)',
    fg: '#1e40af',
    darkFg: '#93c5fd',
    Icon: InfoOutlinedIcon,
  },
};

function IssueBanner({ issue, palette, isDark }) {
  const colors = BANNER_COLORS[issue.level] || BANNER_COLORS[ISSUE_LEVEL.INFO];
  const Icon = colors.Icon;
  const color = isDark ? colors.darkFg : colors.fg;
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '8px',
        alignItems: 'flex-start',
        px: '10px',
        py: '8px',
        backgroundColor: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: NODE_UI.borderRadiusSm,
      }}
    >
      <Icon sx={{ fontSize: 16, color, mt: '1px', flexShrink: 0 }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: NODE_UI.fontSize.label, color, lineHeight: 1.45 }}>
          {issue.message}
        </Typography>
        {Array.isArray(issue.offendingAssignees) && issue.offendingAssignees.length > 0 ? (
          <Typography
            sx={{
              fontSize: NODE_UI.fontSize.caption,
              color: palette.textSecondary,
              opacity: 0.85,
              mt: '2px',
              fontFamily: 'monospace',
              wordBreak: 'break-all',
            }}
          >
            {issue.offendingAssignees.join(', ')}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

function GuardrailBanners({ issues, palette, isDark }) {
  if (!Array.isArray(issues) || issues.length === 0) return null;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', mb: '4px' }}>
      {issues.map((issue, idx) => (
        <IssueBanner
          key={`${issue.code}-${idx}`}
          issue={issue}
          palette={palette}
          isDark={isDark}
        />
      ))}
    </Box>
  );
}

/* ── Field label ─────────────────────────────────────────────────────── */

function FieldLabel({ children, hint, palette }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mb: '4px' }}>
      <Typography
        sx={{
          fontSize: NODE_UI.fontSize.label,
          fontWeight: NODE_UI.fontWeight.label,
          color: palette.textSecondary,
          lineHeight: 1.4,
          userSelect: 'none',
        }}
      >
        {children}
      </Typography>
      {hint && (
        <Tooltip title={hint} arrow placement="top">
          <InfoOutlinedIcon sx={{ fontSize: 13, color: palette.textSecondary, opacity: 0.5, cursor: 'help' }} />
        </Tooltip>
      )}
    </Box>
  );
}

function SectionSelect({ label, value, onChange, options, theme, palette }) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <FieldLabel palette={palette}>{label}</FieldLabel>
      <Select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        sx={{
          fontFamily: 'inherit',
          fontSize: NODE_UI.fontSize.value,
          color: palette.textPrimary,
          backgroundColor: theme.palette.background.default,
          borderRadius: NODE_UI.borderRadiusSm,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.textSecondary,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: palette.textPrimary,
          },
          '& .MuiSelect-select': {
            padding: NODE_UI.input.padding,
          },
        }}
      >
        {options.map((o) => (
          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
        ))}
      </Select>
    </Box>
  );
}

/* ── Mode toggle ─────────────────────────────────────────────────────── */

function AssignmentModeToggle({ activeMode, onChange, palette, theme }) {
  return (
    <Box>
      <FieldLabel palette={palette}>Assignment mode</FieldLabel>
      <ToggleButtonGroup
        value={activeMode}
        exclusive
        onChange={(_, val) => { if (val) onChange(val); }}
        size="small"
        fullWidth
        sx={{
          '& .MuiToggleButtonGroup-grouped': {
            border: `1px solid ${palette.border}`,
            borderRadius: `${NODE_UI.borderRadius} !important`,
            mx: '2px',
            '&:first-of-type': { ml: 0 },
            '&:last-of-type': { mr: 0 },
            textTransform: 'none',
            fontSize: NODE_UI.fontSize.label,
            fontWeight: 500,
            color: palette.textSecondary,
            py: '5px',
            px: '8px',
            gap: '5px',
            transition: 'all 0.15s ease',
            '&.Mui-selected': {
              backgroundColor: palette.surfaceSubtle,
              color: palette.textPrimary,
              fontWeight: 600,
              borderColor: palette.textSecondary,
              '&:hover': {
                backgroundColor: palette.surfaceSubtle,
              },
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          },
        }}
      >
        {UI_MODE_CONFIG.map(({ value, label, icon: Icon }) => (
          <ToggleButton key={value} value={value} disableRipple>
            <Icon sx={{ fontSize: 15 }} />
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
      <Typography
        sx={{
          fontSize: NODE_UI.fontSize.caption,
          color: palette.textSecondary,
          opacity: 0.6,
          mt: '4px',
        }}
      >
        {UI_MODE_CONFIG.find((m) => m.value === activeMode)?.hint}
      </Typography>
    </Box>
  );
}

/* ── Specific Users panel ─────────────────────────────────────────────
 * Unified picker for ONE_PERSON + MANY_PEOPLE. Both backend modes are
 * identical at runtime (they both reduce to a flat assignee list); the only
 * thing that differs is the activeMode label and a legacy `assigneeUserId`
 * string. We read from whichever slot the backend wrote last and, on save,
 * derive the activeMode from the assignee count:
 *   0 or 1 assignees → ONE_PERSON (keeps the legacy `assigneeUserId` string)
 *   2+ assignees     → MANY_PEOPLE
 *
 * We also keep both `onePerson.assigneeUserIds` and `manyPeople.assigneeUserIds`
 * in sync so mode switches are lossless and downstream readers that only look
 * at one slot always see the current list.
 */

function readSpecificAssignees(responsibility) {
  const onePerson = responsibility?.onePerson || {};
  const manyPeople = responsibility?.manyPeople || {};
  // Prefer whichever slot matches the persisted activeMode, then fall back
  // to any non-empty list from either slot so we never drop data on mode
  // transitions or legacy configs.
  const mode = responsibility?.activeMode;
  const primary = mode === BACKEND_MODES.MANY_PEOPLE ? manyPeople : onePerson;
  const fallback = mode === BACKEND_MODES.MANY_PEOPLE ? onePerson : manyPeople;

  const fromArray = (obj) => (Array.isArray(obj?.assigneeUserIds) ? obj.assigneeUserIds : []);
  const fromLegacy = (obj) => (obj?.assigneeUserId ? [obj.assigneeUserId] : []);

  const primaryList = fromArray(primary).length ? fromArray(primary) : fromLegacy(primary);
  if (primaryList.length) return primaryList.filter(Boolean);
  const fallbackList = fromArray(fallback).length ? fromArray(fallback) : fromLegacy(fallback);
  return fallbackList.filter(Boolean);
}

function SpecificUsersPanel({ responsibility, onUpdate, users, usersLoading, palette, availablePlaceholders }) {
  const currentIds = useMemo(() => readSpecificAssignees(responsibility), [responsibility]);

  const handleChange = useCallback(
    (nextFlat) => {
      const next = Array.isArray(nextFlat) ? nextFlat.filter(Boolean) : [];
      const nextMode = backendModeForSpecificUsers(next.length);
      onUpdate({
        ...responsibility,
        activeMode: nextMode,
        onePerson: {
          ...(responsibility?.onePerson || {}),
          assigneeUserId: next[0] || '',
          assigneeUserIds: next.slice(0, 1),
        },
        manyPeople: {
          ...(responsibility?.manyPeople || {}),
          assigneeUserIds: next,
        },
      });
    },
    [responsibility, onUpdate]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      <FieldLabel
        palette={palette}
        hint="Add one or more people. Pick users, type emails, or use $ for a runtime placeholder."
      >
        Assignees
      </FieldLabel>
      <NodeEmailRecipientPicker
        users={users}
        usersLoading={usersLoading}
        value={currentIds}
        onChange={handleChange}
        availablePlaceholders={availablePlaceholders}
        palette={palette}
        placeholder="Search users, type an email, or $ for a placeholder..."
      />
    </Box>
  );
}

/* ── Anyone Eligible panel ───────────────────────────────────────────── */

function AnyoneEligiblePanel({ responsibility, onUpdate, users, usersLoading, palette, availablePlaceholders }) {
  const anyoneEligible = responsibility.anyoneEligible || {};
  const eligibleIds = Array.isArray(anyoneEligible.eligibleUserIds) ? anyoneEligible.eligibleUserIds : [];
  const allowAnonymous = Boolean(anyoneEligible.allowAnonymousWhenPublicLink);

  const handleChange = useCallback(
    (nextFlat) => {
      onUpdate({
        ...responsibility,
        anyoneEligible: {
          ...anyoneEligible,
          eligibleUserIds: Array.isArray(nextFlat) ? nextFlat.filter(Boolean) : [],
        },
      });
    },
    [anyoneEligible, responsibility, onUpdate]
  );

  const handleAnonymousToggle = useCallback((e) => {
    onUpdate({
      ...responsibility,
      anyoneEligible: {
        ...anyoneEligible,
        allowAnonymousWhenPublicLink: e.target.checked,
        ...(e.target.checked ? { eligibleUserIds: [] } : {}),
      },
    });
  }, [anyoneEligible, responsibility, onUpdate]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {!allowAnonymous && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <FieldLabel
            palette={palette}
            hint="Any user in this pool can claim the task. Leave empty to allow every team member."
          >
            Eligible users
          </FieldLabel>
          <NodeEmailRecipientPicker
            users={users}
            usersLoading={usersLoading}
            value={eligibleIds}
            onChange={handleChange}
            availablePlaceholders={availablePlaceholders}
            palette={palette}
            placeholder="Search users, type an email, or $ for a placeholder..."
          />
        </Box>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={allowAnonymous}
            onChange={handleAnonymousToggle}
            size="small"
            sx={{
              color: palette.border,
              '&.Mui-checked': { color: palette.textPrimary },
              p: '4px',
            }}
          />
        }
        label={
          <Typography sx={{ fontSize: NODE_UI.fontSize.label, color: palette.textSecondary }}>
            Allow anonymous submissions via public link
          </Typography>
        }
        sx={{ ml: -0.5, gap: '2px' }}
      />
    </Box>
  );
}

export function ReviewTaskAssignmentSettings({
  responsibility = {},
  onChange,
  accessPolicy,
  onAccessPolicyChange,
  accessModes,
  accessIssues,
  isDark = false,
  users,
  usersLoading,
  availablePlaceholders = [],
}) {
  const palette = useFormPalette();
  const theme = useTheme();
  const activeAccountUsers = useTaskInboxStore((s) => s.activeAccountUsers);
  const activeAccountUsersLoading = useTaskInboxStore((s) => s.activeAccountUsersLoading);
  const fetchActiveAccountUsers = useTaskInboxStore((s) => s.fetchActiveAccountUsers);

  useEffect(() => {
    if (!users) {
      fetchActiveAccountUsers();
    }
  }, [fetchActiveAccountUsers, users]);

  const resolvedUsers = users || activeAccountUsers;
  const resolvedUsersLoading = usersLoading ?? activeAccountUsersLoading;
  const normalizedResponsibility = responsibility || {};
  const uiMode = toUiMode(normalizedResponsibility.activeMode);

  const handleUiModeChange = useCallback(
    (nextUiMode) => {
      if (nextUiMode === UI_MODES.ANYONE_ELIGIBLE) {
        onChange?.({
          ...normalizedResponsibility,
          activeMode: BACKEND_MODES.ANYONE_ELIGIBLE,
        });
        return;
      }
      const assignees = readSpecificAssignees(normalizedResponsibility);
      onChange?.({
        ...normalizedResponsibility,
        activeMode: backendModeForSpecificUsers(assignees.length),
      });
    },
    [normalizedResponsibility, onChange]
  );

  const assignmentPanel =
    uiMode === UI_MODES.ANYONE_ELIGIBLE ? (
      <AnyoneEligiblePanel
        responsibility={normalizedResponsibility}
        onUpdate={onChange}
        users={resolvedUsers}
        usersLoading={resolvedUsersLoading}
        palette={palette}
        availablePlaceholders={availablePlaceholders}
      />
    ) : (
      <SpecificUsersPanel
        responsibility={normalizedResponsibility}
        onUpdate={onChange}
        users={resolvedUsers}
        usersLoading={resolvedUsersLoading}
        palette={palette}
        availablePlaceholders={availablePlaceholders}
      />
    );

  return (
    <SettingsSection title="Assignment" palette={palette}>
      {onAccessPolicyChange ? (
        <PublicAccessPolicyEditor
          accessPolicy={accessPolicy}
          onChange={onAccessPolicyChange}
          users={resolvedUsers}
          usersLoading={resolvedUsersLoading}
          modes={accessModes}
        />
      ) : null}
      <GuardrailBanners issues={accessIssues} palette={palette} isDark={isDark} />
      <AssignmentModeToggle
        activeMode={uiMode}
        onChange={handleUiModeChange}
        palette={palette}
        theme={theme}
      />
      {assignmentPanel}
    </SettingsSection>
  );
}

const REVIEW_TASK_TABS = [
  { value: 0, label: 'Task Form' },
  { value: 1, label: 'Settings' },
];

/* ── Main component ──────────────────────────────────────────────────── */

export default function DynamicReviewTaskTemplate({
  component_id,
  projectId,
  setChildTemplateLoaded,
  availablePlaceholders,
  standalone = false,
  /** When set with standalone, parent owns tab navigation (no inner tab bar). */
  section,
}) {
  const store = useMemo(() => getDynamicReviewTaskStore(component_id), [component_id]);
  const data = store((s) => s.data);
  const loaded = store((s) => s.loaded);
  const componentName = store((s) => s.name);
  const palette = useFormPalette();
  const theme = useTheme();

  const [internalTab, setInternalTab] = useState(0);

  /* Fetch team members */
  const activeAccountUsers = useTaskInboxStore((s) => s.activeAccountUsers);
  const activeAccountUsersLoading = useTaskInboxStore((s) => s.activeAccountUsersLoading);
  const fetchActiveAccountUsers = useTaskInboxStore((s) => s.fetchActiveAccountUsers);

  useEffect(() => {
    fetchActiveAccountUsers();
  }, [fetchActiveAccountUsers]);

  useEffect(() => {
    if (loaded && setChildTemplateLoaded) setChildTemplateLoaded(true);
  }, [loaded, setChildTemplateLoaded]);

  // Email body supports the system-level !{taskURL} token which the backend
  // expands to the review-task link at send time. We register it as a
  // form_placeholder so the TipTap editor validates the `!{taskURL}` chip
  // (rather than rendering it as a broken placeholder) and autocompletes
  // it when the user types `!`.
  const emailBodyPlaceholders = useMemo(() => {
    const taskUrlEntry = { key: 'taskURL', type: 'form_placeholder' };
    if (!Array.isArray(availablePlaceholders)) return [taskUrlEntry];
    const nested = availablePlaceholders[0]?.placeholders;
    if (Array.isArray(nested)) {
      return [
        {
          ...availablePlaceholders[0],
          placeholders: [...nested, taskUrlEntry],
        },
        ...availablePlaceholders.slice(1),
      ];
    }
    return [...availablePlaceholders, taskUrlEntry];
  }, [availablePlaceholders]);

  /* Store actions */
  const handleFieldsChange = useCallback(
    (fields) => store.getState().setFields(fields),
    [store]
  );
  const handleTitleChange = useCallback(
    (val) => store.getState().setTitle(val),
    [store]
  );
  const handleObjectiveChange = useCallback(
    (val) => store.getState().setObjective(val),
    [store]
  );
  const handleDescriptionChange = useCallback(
    (val) => store.getState().setDescription(val),
    [store]
  );
  const handleAccessPolicyChange = useCallback(
    (val) => store.getState().setAccessPolicy(val),
    [store]
  );
  const handleResponseModeChange = useCallback(
    (val) => store.getState().setResponseMode(val),
    [store]
  );

  /* ── Responsibility helpers ──────────────────────────────────────── */

  const responsibility = useMemo(() => getResponsibility(data.taskConfig), [data.taskConfig]);

  // Cross-validate access gate + assignment. These are the same rules the
  // agent tool (`review_task`) enforces server-side; we show them inline so
  // builders catch problems before they hit the form's UI.
  const accessIssues = useMemo(
    () =>
      validateReviewTaskAccess({
        accessPolicy: data.accessPolicy,
        responsibility,
      }),
    [data.accessPolicy, responsibility]
  );
  const isDarkTheme = theme?.palette?.mode === 'dark';

  // Panels own their own activeMode transitions; persist the updated
  // flips ONE_PERSON ↔ MANY_PEOPLE based on count), so we just persist the
  // updated responsibility object verbatim.
  const handleResponsibilityUpdate = useCallback(
    (updated) => {
      store.getState().setTaskConfig({ responsibility: updated });
    },
    [store]
  );

  /* ── Email notify ────────────────────────────────────────────────── */

  const emailConfig = data.taskConfig?.notifications?.email || {};
  const emailNotifyEnabled = Boolean(emailConfig.enabled);
  const emailNotifyUserIds = Array.isArray(emailConfig.userIds) ? emailConfig.userIds : [];
  const emailNotifyToEmails = Array.isArray(emailConfig.toEmails) ? emailConfig.toEmails : [];
  const emailNotifyCcUserIds = Array.isArray(emailConfig.ccUserIds) ? emailConfig.ccUserIds : [];
  const emailNotifyCcEmails = Array.isArray(emailConfig.ccEmails) ? emailConfig.ccEmails : [];
  const emailNotifyBccUserIds = Array.isArray(emailConfig.bccUserIds) ? emailConfig.bccUserIds : [];
  const emailNotifyBccEmails = Array.isArray(emailConfig.bccEmails) ? emailConfig.bccEmails : [];
  const emailNotifySubject = typeof emailConfig.subject === 'string' ? emailConfig.subject : '';
  const emailNotifyBody = typeof emailConfig.body === 'string' ? emailConfig.body : '';

  const handleCoverImageChange = useCallback(
    (val) => store.getState().setFormSettings({ coverImageUrl: val }),
    [store]
  );
  const handleCreationOutputKeyChange = useCallback(
    (key) => store.getState().setCreationOutputPlaceholderKey(key),
    [store]
  );

  const handleFlowSettingsChange = useCallback(
    (patch) => store.getState().setFlowSettings(patch),
    [store]
  );
  const handleStepperTitleChange = useCallback(
    (val) => store.getState().setStepperTitle(val),
    [store]
  );
  const upstreamFlowEntries = useUpstreamFlowEntries(component_id);

  // Read fresh state instead of closing over `data.taskConfig`. The email
  // recipient picker fires `onUserIdsChange` and `onEmailsChange` back to
  // back inside one click, so a closure over render-time data lets the
  // second call overwrite `email` with a stale spread that erases the
  // first call's update (e.g. the userIds we just added). Reading from the
  // store on every invocation keeps both partials composable.
  const handleUpdateEmailConfig = useCallback(
    (partial) => {
      const taskConfigNow = store.getState().data?.taskConfig || {};
      store.getState().setTaskConfig({
        notifications: {
          ...taskConfigNow.notifications,
          email: {
            ...(taskConfigNow.notifications?.email || {}),
            ...partial,
          },
        },
      });
    },
    [store]
  );

  const handleEmailToggle = useCallback(
    (e) => {
      const enabled = e.target.checked;
      if (!enabled) {
        handleUpdateEmailConfig({
          enabled: false,
          userIds: [],
          toEmails: [],
          ccUserIds: [],
          ccEmails: [],
          bccUserIds: [],
          bccEmails: [],
          subject: '',
          body: '',
        });
        return;
      }
      // Enabling: pre-fill subject/body with editable template text derived
      // from the current title/objective, but only when the user hasn't
      // already typed their own content.
      const nextSubject =
        emailNotifySubject && emailNotifySubject.trim()
          ? emailNotifySubject
          : buildDefaultEmailSubject(data.title);
      const nextBody =
        emailNotifyBody && emailNotifyBody.trim()
          ? emailNotifyBody
          : buildDefaultEmailBody(data.objective);
      handleUpdateEmailConfig({
        enabled: true,
        subject: nextSubject,
        body: nextBody,
      });
    },
    [handleUpdateEmailConfig, emailNotifySubject, emailNotifyBody, data.title, data.objective]
  );

  // Backfill subject/body for legacy configs that had notifications enabled
  // before the composer existed. Runs once when we observe the enabled state
  // with blank fields; user edits are respected thereafter.
  useEffect(() => {
    if (!emailNotifyEnabled) return;
    const needsSubject = !emailNotifySubject || !emailNotifySubject.trim();
    const needsBody = !emailNotifyBody || !emailNotifyBody.trim();
    if (!needsSubject && !needsBody) return;
    const patch = {};
    if (needsSubject) patch.subject = buildDefaultEmailSubject(data.title);
    if (needsBody) patch.body = buildDefaultEmailBody(data.objective);
    handleUpdateEmailConfig(patch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailNotifyEnabled]);

  const handleEmailUserIdsChange = useCallback(
    (userIds) => handleUpdateEmailConfig({ userIds }),
    [handleUpdateEmailConfig]
  );
  const handleEmailToEmailsChange = useCallback(
    (toEmails) => handleUpdateEmailConfig({ toEmails }),
    [handleUpdateEmailConfig]
  );
  const handleEmailCcUserIdsChange = useCallback(
    (ccUserIds) => handleUpdateEmailConfig({ ccUserIds }),
    [handleUpdateEmailConfig]
  );
  const handleEmailCcEmailsChange = useCallback(
    (ccEmails) => handleUpdateEmailConfig({ ccEmails }),
    [handleUpdateEmailConfig]
  );
  const handleEmailBccUserIdsChange = useCallback(
    (bccUserIds) => handleUpdateEmailConfig({ bccUserIds }),
    [handleUpdateEmailConfig]
  );
  const handleEmailBccEmailsChange = useCallback(
    (bccEmails) => handleUpdateEmailConfig({ bccEmails }),
    [handleUpdateEmailConfig]
  );
  const handleEmailSubjectChange = useCallback(
    (subject) => handleUpdateEmailConfig({ subject: subject || '' }),
    [handleUpdateEmailConfig]
  );
  const handleEmailBodyChange = useCallback(
    (body) => handleUpdateEmailConfig({ body: body || '' }),
    [handleUpdateEmailConfig]
  );

  if (!loaded) return null;

  const objectiveField = (
    <Box sx={{ mt: 1 }}>
      <InputBase
        value={data.objective || ''}
        onChange={(e) => handleObjectiveChange(e.target.value)}
        placeholder="What the assignee needs to do (objective)..."
        multiline
        minRows={1}
        fullWidth
        sx={{
          fontFamily: 'inherit',
          fontSize: '12px',
          color: palette.textPrimary,
          border: `1px dashed ${palette.border}`,
          borderRadius: '4px',
          padding: '6px 8px',
          lineHeight: 1.5,
          transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
          '&:hover': { borderColor: palette.textSecondary },
          '&.Mui-focused': { borderColor: palette.textPrimary, borderStyle: 'solid' },
          '& textarea::placeholder': { color: palette.textSecondary, opacity: 0.4 },
        }}
      />
    </Box>
  );

  /* ── Assignment mode panel ───────────────────────────────────────── */

  const externalSection = standalone && section;
  const showTabBar = !externalSection;
  const activeSection = externalSection || (internalTab === 0 ? 'design' : 'settings');

  // TEMPLATE mode: fields are owned by a top-level Form Template and resolved at runtime, so the
  // in-canvas field designer is replaced by a read-only notice. Assignment / notifications stay
  // editable here as workflow-specific overrides.
  const isTemplateMode = !standalone && data.sourceMode === 'TEMPLATE';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {showTabBar && (
        <W86SegmentedTabs
          tabs={REVIEW_TASK_TABS}
          value={internalTab}
          onChange={setInternalTab}
          variant="fill"
          ariaLabel="Review task sections"
        />
      )}

      {activeSection === 'design' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <ReviewTaskAssignmentSettings
            responsibility={responsibility}
            onChange={handleResponsibilityUpdate}
            accessPolicy={data.accessPolicy}
            onAccessPolicyChange={handleAccessPolicyChange}
            accessIssues={accessIssues}
            isDark={isDarkTheme}
            users={activeAccountUsers}
            usersLoading={activeAccountUsersLoading}
            availablePlaceholders={availablePlaceholders}
          />

          {/* ── Notifications section ──────────────────────────────── */}
          <SettingsSection title="Notifications" palette={palette}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={emailNotifyEnabled}
                  onChange={handleEmailToggle}
                  size="small"
                  sx={{
                    color: palette.border,
                    '&.Mui-checked': { color: palette.textPrimary },
                    p: '4px',
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: NODE_UI.fontSize.value, fontWeight: 500, color: palette.textPrimary }}>
                  Send email notifications
                </Typography>
              }
              sx={{ ml: -0.5, gap: '2px' }}
            />
            <Typography
              sx={{
                fontSize: NODE_UI.fontSize.caption,
                color: palette.textSecondary,
                opacity: 0.7,
                mt: '-2px',
                ml: '26px',
              }}
            >
              {standalone
                ? 'When enabled, selected recipients are emailed when you send this task.'
                : 'When enabled, selected recipients are emailed when the task is created.'}
            </Typography>

            {emailNotifyEnabled && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mt: '6px' }}>
                <NodeEmailRecipientPicker
                  label="To"
                  users={activeAccountUsers}
                  usersLoading={activeAccountUsersLoading}
                  selectedUserIds={emailNotifyUserIds}
                  selectedEmails={emailNotifyToEmails}
                  onUserIdsChange={handleEmailUserIdsChange}
                  onEmailsChange={handleEmailToEmailsChange}
                  availablePlaceholders={availablePlaceholders}
                  palette={palette}
                  placeholder="Search users, type an email, or $ for a placeholder..."
                />
                <NodeEmailRecipientPicker
                  label="Cc"
                  users={activeAccountUsers}
                  usersLoading={activeAccountUsersLoading}
                  selectedUserIds={emailNotifyCcUserIds}
                  selectedEmails={emailNotifyCcEmails}
                  onUserIdsChange={handleEmailCcUserIdsChange}
                  onEmailsChange={handleEmailCcEmailsChange}
                  availablePlaceholders={availablePlaceholders}
                  palette={palette}
                  placeholder="Add Cc users, emails, or $ placeholders..."
                />
                <NodeEmailRecipientPicker
                  label="Bcc"
                  users={activeAccountUsers}
                  usersLoading={activeAccountUsersLoading}
                  selectedUserIds={emailNotifyBccUserIds}
                  selectedEmails={emailNotifyBccEmails}
                  onUserIdsChange={handleEmailBccUserIdsChange}
                  onEmailsChange={handleEmailBccEmailsChange}
                  availablePlaceholders={availablePlaceholders}
                  palette={palette}
                  placeholder="Add Bcc users, emails, or $ placeholders..."
                />
                <NodePlaceholderAutocomplete
                  label="Subject"
                  value={emailNotifySubject}
                  onChange={handleEmailSubjectChange}
                  availablePlaceholders={availablePlaceholders}
                  placeholder="Email subject (type $ to insert a placeholder)"
                />
                <NodeRichTextEditor
                  label="Body"
                  value={emailNotifyBody}
                  onChange={handleEmailBodyChange}
                  availablePlaceholders={emailBodyPlaceholders}
                  triggerChars={['$', '!']}
                  placeholder="Email body. Type ! to insert the review-task link (!{taskURL}) or $ to insert a workflow placeholder."
                  minHeight="140px"
                />
              </Box>
            )}
            {!standalone && (
              <Typography
                sx={{
                  fontSize: NODE_UI.fontSize.caption,
                  color: palette.textSecondary,
                  opacity: 0.7,
                  mt: '8px',
                }}
              >
                Need more than email? Drag from the <strong>On creation</strong> port to send Slack/SMS,
                call a webhook, write to a database, or run any other action when the task is created.
              </Typography>
            )}
          </SettingsSection>

          {isTemplateMode ? (
            <SettingsSection title="Fields" palette={palette}>
              <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: palette.textSecondary }}>
                Fields are defined by a top-level template and resolved at runtime. Edit the fields
                on the Forms page.
              </Typography>
              {data.formTemplateId && (
                <Typography
                  component="a"
                  href={`/forms/${data.formTemplateId}`}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ fontSize: NODE_UI.fontSize.caption, color: 'primary.main', mt: '4px', display: 'inline-block' }}
                >
                  Open template ↗
                </Typography>
              )}
            </SettingsSection>
          ) : (
            <DynamicFormFieldDesigner
              fields={data.fields}
              onChange={handleFieldsChange}
              title={data.title}
              description={data.description}
              onTitleChange={handleTitleChange}
              onDescriptionChange={handleDescriptionChange}
              headerExtra={objectiveField}
              availablePlaceholders={availablePlaceholders}
              surface={standalone ? 'public' : 'classic'}
              coverImageUrl={data.formSettings?.coverImageUrl || ''}
              onCoverImageChange={handleCoverImageChange}
              formId={component_id}
            />
          )}
        </Box>
      )}

      {activeSection === 'settings' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <SettingsSection title="General" palette={palette}>
            <SectionSelect
              label="Response Mode"
              value={data.responseMode}
              onChange={handleResponseModeChange}
              options={[
                { value: 'FILL_SUBMIT', label: 'Submit only' },
                { value: 'FILL_REEDIT', label: 'Save & resume drafts' },
                { value: 'VIEW_ONLY', label: 'View only' },
              ]}
              theme={theme}
              palette={palette}
            />
            <Typography sx={{ fontSize: NODE_UI.fontSize.caption, color: palette.textSecondary, opacity: 0.75 }}>
              {RESPONSE_MODE_DESCRIPTIONS[data.responseMode] || ''}
            </Typography>
          </SettingsSection>

          {!standalone && (
            <SettingsSection title="On creation output" palette={palette}>
              <CreationOutputPlaceholderField
                componentType="dynamic_review_task"
                value={data.creationOutputPlaceholder}
                onChange={handleCreationOutputKeyChange}
              />
            </SettingsSection>
          )}

          {/* ── Appearance section (canvas only; standalone uses cover hover popover) ── */}
          {!standalone && (
            <SettingsSection title="Appearance" palette={palette}>
              <CoverImageSetting
                value={data.formSettings?.coverImageUrl || ''}
                onChange={handleCoverImageChange}
              />
            </SettingsSection>
          )}

          {!standalone && (
            <SettingsSection title="Flow" palette={palette}>
              <FlowSettingsBlock
                componentId={component_id}
                componentName={componentName}
                componentData={data}
                upstreamFlowEntries={upstreamFlowEntries}
                onChange={handleFlowSettingsChange}
              />
              <StepperTitleInput
                value={data.stepperTitle}
                onChange={handleStepperTitleChange}
              />
            </SettingsSection>
          )}
        </Box>
      )}
    </Box>
  );
}
